import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import {
  PageContainer, StatCard, PremiumCard, DataTable, StatusBadge,
  LoadingState, EmptyState,
} from "components/ui";

const IconTag = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const IconBasket = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 11 12 2l7 9" /><path d="M2 11h20l-2 11H4z" />
  </svg>
);
const IconActivity = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const isLowStock = (item) => item.quantity <= (item.minStockLevel ?? 5);

const StockManagerDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user, loading: userLoading } = useCurrentUser();
  const branch = user?.branch;

  const [stockProducts, setStockProducts] = useState([]);
  const [recentMovements, setRecentMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!branch) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [stockRes, movRes] = await Promise.all([
          axiosPrivate.get(`/api/branches/${branch.id}/stock`),
          axiosPrivate.get(`/api/branches/${branch.id}/movements?size=5`),
        ]);
        setStockProducts(stockRes.data);
        setRecentMovements(movRes.data.content || movRes.data);
      } catch (err) {
        console.error("Failed to fetch stock data", err);
        toast.error("Failed to load stock overview");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [branch]);

  if (userLoading || loading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading stock overview…" /></PremiumCard>
      </PageContainer>
    );
  }

  if (!branch) {
    return (
      <PageContainer>
        <PremiumCard>
          <EmptyState title="No branch assigned" description="Ask your branch admin to assign you to a warehouse." />
        </PremiumCard>
      </PageContainer>
    );
  }

  const lowStockCount = stockProducts.filter(isLowStock).length;

  return (
    <PageContainer>
      <PremiumCard
        className="mb-4"
        title={branch.name}
        subtitle={[branch.address, branch.city].filter(Boolean).join(" · ") || "No address"}
        actions={<StatusBadge value={branch.status || "ACTIVE"} dot />}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard label="Total products"    value={stockProducts.length} icon={<IconTag />} />
        <StatCard
          label="Low-stock items"
          value={lowStockCount}
          icon={<IconBasket />}
          accent={lowStockCount > 0 ? { bg: "var(--warning-bg)", fg: "var(--warning)" } : undefined}
        />
        <StatCard label="Recent movements" value={recentMovements.length} icon={<IconActivity />} />
      </div>

      <DataTable
        title="Product stock"
        subtitle="Current inventory positions"
        data={stockProducts}
        rowKey={(s) => s.id}
        searchPlaceholder="Search products…"
        pageSize={8}
        emptyTitle="No stock records"
        columns={[
          { key: "product",  header: "Product",  render: (s) => s.product?.name || s.name },
          { key: "category", header: "Category", render: (s) => s.product?.category?.name || "—" },
          { key: "sku",      header: "SKU",      render: (s) => <span className="t-mono t-muted">{s.product?.sku || "—"}</span> },
          {
            key: "quantity", header: "Qty", align: "right",
            render: (s) => <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>{s.quantity}</span>,
          },
          {
            key: "status", header: "Status",
            render: (s) =>
              isLowStock(s)
                ? <StatusBadge tone="warning">LOW STOCK</StatusBadge>
                : <StatusBadge tone="success">IN STOCK</StatusBadge>,
          },
        ]}
      />

      <div style={{ marginTop: 16 }}>
        <DataTable
          title="Recent stock movements"
          subtitle="Last 5 changes"
          data={recentMovements}
          rowKey={(m) => m.id}
          search={false}
          paginate={false}
          emptyTitle="No movements yet"
          columns={[
            { key: "product", header: "Product", render: (m) => m.product?.name || "—" },
            {
              key: "qty", header: "Change", align: "right",
              render: (m) => (
                <span className={m.quantityChange > 0 ? "t-success" : "t-danger"} style={{ fontWeight: 600, fontFamily: "var(--font-mono)" }}>
                  {m.quantityChange > 0 ? "+" : ""}{m.quantityChange}
                </span>
              ),
            },
            { key: "reason", header: "Type", render: (m) => <StatusBadge value={m.reason} /> },
            { key: "createdAt", header: "Date", render: (m) => <span className="t-muted">{new Date(m.createdAt).toLocaleString()}</span> },
          ]}
        />
      </div>
    </PageContainer>
  );
};

export default StockManagerDashboard;